import { z } from "zod";
import { CapSchema } from "./capabilities.ts";
import { EventKindSchema, EventPayloadSchema } from "./events.ts";
import {
  ActionSummarySchema,
  AskableCapSchema,
  LocalNameSchema,
  PluginEntrySchema,
  PluginIdSchema,
  PluginManifestSchema,
  PluginDataVersionSchema,
  type ActionDenialRule,
} from "./plugin.ts";
import { PrincipalSchema } from "./principal.ts";
import { ManifoldRefSchema } from "./uri.ts";
import { StreamServerMessageSchema } from "./stream.ts";
import { JobFollowUpdateSchema, SettledJobSchema, machineArtifacts } from "./jobs.ts";
import {
  AgentSchema,
  HarnessDefinitionSchema,
  HarnessTargetSchema,
  RunModelSchema,
} from "./agents.ts";
import { AgentRunSchema, SendRunInputRequestSchema } from "./agent-runs.ts";
import { SessionRefSchema } from "./session-ref.ts";
import { TerminalRuntimeSchema, type TerminalRuntime } from "./jobs.ts";
import { GrantNodeSchema, GrantReachSchema } from "./grants.ts";
import { TerminalExecutionSchema } from "./machine.ts";
import { PanelArgSchema, validPanelArg } from "./layout.ts";

/**
 * THE ISOLATION VOCABULARY (ADR 0016): everything that crosses the boundary between the engine
 * and a plugin it does not trust with its own realm.
 *
 * A hardened plugin runs its server half in its own OS process and its web half in its own
 * dedicated Worker (ADR 0016 §1). Both boundaries are message boundaries, so what crosses them
 * is wire, and wire lives here (docs/CONTRACTS.md §Protocol and compatibility): the bounded
 * newline-delimited JSON frames a supervisor and a child exchange over a dedicated socket, the
 * frames a panel host and a Worker exchange over `postMessage`, the closed component vocabulary
 * an isolated web half renders with (§3), the artifact installed from (§8 stage 2), and the
 * numbers that bound a runner's patience (§6).
 *
 * Nothing in this file names a plugin, a panel or a host class: the same three-way neutrality
 * the rest of the protocol keeps. The host selects execution independently of whether a
 * definition came from its trusted build or an admitted installation.
 */

// ---------------------------------------------------------------------------- UI vocabulary

/**
 * THE TONES a vocabulary node may ask for. Five words, none of them a colour: an isolated
 * plugin says what a thing MEANS and the host's theme decides how that looks, which is what
 * keeps ink under one owner (S13) while a stranger's panel still gets to say "this is the
 * dangerous button".
 */
export const UI_TONES = ["neutral", "accent", "muted", "danger", "success"] as const;
export const UiToneSchema = z.enum(UI_TONES);
export type UiTone = (typeof UI_TONES)[number];

/** The existing host-owned control glyph names, shared by native and frame rendering. */
export const UI_CONTROL_KINDS = [
  "park",
  "maximize",
  "shrink",
  "close",
  "confirm",
  "cancel",
  "add",
  "more",
  "disclosed",
  "collapsed",
  "sidebarCollapse",
  "sidebarExpand",
  "reveal",
  "discard",
  "revoke",
  "restart",
  "equalize",
  "grip",
  "locked",
  "takeControl",
  "assembly",
  "nesting",
  "scopeIn",
  "bindings",
  "swap",
  "settings",
] as const;
export const UiIconSchema = z.discriminatedUnion("family", [
  z.strictObject({
    family: z.literal("control"),
    name: z.enum(UI_CONTROL_KINDS),
    size: z.number().int().min(8).max(32).optional(),
  }),
  z.strictObject({
    family: z.literal("item"),
    name: z.string().min(1).max(128),
    size: z.number().int().min(8).max(32).optional(),
  }),
]);
export type UiIcon = z.infer<typeof UiIconSchema>;

/**
 * The node kinds of the closed, host-owned component vocabulary (ADR 0016 §3, R2). An isolated
 * web half renders by SENDING A TREE of these — serialized components in, named callbacks
 * out, the MetaMask Snaps shape — and never touches the DOM by any route. The set is closed
 * on purpose: every kind here has exactly one renderer, in the engine, painting one CSS family
 * the engine owns; a kind the host does not know is refused at the schema, never rendered as
 * "unknown".
 */
export const UI_NODE_TYPES = [
  "box",
  "heading",
  "text",
  "code",
  "badge",
  "icon",
  "divider",
  "spinner",
  "button",
  "select",
  "input",
  "toggle",
  "list",
  "empty",
] as const satisfies readonly UiNode["type"][];
export type UiNodeType = (typeof UI_NODE_TYPES)[number];

/**
 * How deep a tree may nest and how many nodes it may carry. A render is one `postMessage`
 * per frame the worker chooses to paint, so the bound is what the main thread can honestly
 * diff and lay out without the worker being able to wedge it — the same reason a section
 * arrangement has `MAX_SECTION_DEPTH`. Refused rather than truncated: a tree the host cannot
 * paint is not one the worker should believe was painted.
 */
export const MAX_UI_DEPTH = 32;
export const MAX_UI_NODES = 2000;

/** The longest run of prose one node carries; a `code` block gets more because it is a dump. */
export const MAX_UI_TEXT_LENGTH = 4096;
export const MAX_UI_CODE_LENGTH = 64 * 1024;
/** Options in one `select`, rows in one `list`: bounded like every contributed list is. */
export const MAX_UI_OPTIONS = 256;
export const MAX_UI_LIST_ITEMS = 500;

/** A named callback: the worker's own word for what a control does, echoed back verbatim. */
const uiEventName = z.string().min(1).max(64);
/** Prose the host paints; never HTML — the renderer sets `textContent`, so this is inert. */
const uiText = z.string().max(MAX_UI_TEXT_LENGTH);

export interface UiSelectOption {
  readonly value: string;
  readonly label: string;
}

export interface UiListItem {
  readonly key: string;
  readonly primary: string;
  readonly secondary?: string | undefined;
  readonly tone?: UiTone | undefined;
  readonly event?: string | undefined;
  readonly payload?: unknown;
}

/** Presentation data, never arbitrary DOM properties or an authority-bearing identifier. */
export interface UiNodeMeta {
  readonly key?: string | undefined;
  readonly title?: string | undefined;
  readonly ariaLabel?: string | undefined;
  readonly testId?: string | undefined;
  readonly role?: "status" | "alert" | undefined;
}
const uiNodeMeta = {
  key: z.string().min(1).max(128).optional(),
  title: uiText.optional(),
  ariaLabel: uiText.optional(),
  testId: z.string().min(1).max(128).optional(),
  role: z.enum(["status", "alert"]).optional(),
} as const;

/**
 * One node of an isolated panel's tree. `button.action` is the FULL action name the button's
 * event ultimately dispatches: the renderer paints it as `data-action`, so a stranger's
 * affordance names the door it opens exactly as a first-party one does (AXIOMS.md §Foundation law and REGISTRY.md §Foundation, S4).
 */
export type UiNode = UiNodeMeta &
  (
    | {
        readonly type: "box";
        readonly direction?: "row" | "column" | undefined;
        /** Numeric and absent gaps retain their legacy meaning; portable layouts name the adaptive default. */
        readonly gap?: 0 | 1 | 2 | 3 | "adaptive" | undefined;
        readonly gapRem?: number | undefined;
        readonly align?: "start" | "center" | "end" | "stretch" | undefined;
        readonly justify?: "start" | "center" | "end" | "between" | undefined;
        readonly grow?: boolean | undefined;
        readonly wrap?: boolean | undefined;
        readonly children: readonly UiNode[];
      }
    | { readonly type: "heading"; readonly text: string; readonly level?: 1 | 2 | 3 | undefined }
    | {
        readonly type: "text";
        readonly text: string;
        readonly tone?: UiTone | undefined;
        readonly mono?: boolean | undefined;
        readonly wrap?: boolean | undefined;
        readonly strong?: boolean | undefined;
        readonly grow?: boolean | undefined;
      }
    | { readonly type: "code"; readonly text: string }
    | { readonly type: "badge"; readonly text: string; readonly tone?: UiTone | undefined }
    | { readonly type: "icon"; readonly icon: UiIcon }
    | { readonly type: "divider" }
    | { readonly type: "spinner"; readonly label?: string | undefined }
    | {
        readonly type: "button";
        readonly label: string;
        readonly event: string;
        readonly blurEvent?: string | undefined;
        readonly payload?: unknown;
        readonly tone?: UiTone | undefined;
        readonly disabled?: boolean | undefined;
        readonly expanded?: boolean | undefined;
        readonly action?: string | undefined;
        readonly icon?: UiIcon | undefined;
        readonly iconOnly?: boolean | undefined;
      }
    | {
        readonly type: "select";
        readonly event: string;
        readonly blurEvent?: string | undefined;
        readonly value: string | null;
        readonly options: readonly UiSelectOption[];
        readonly label?: string | undefined;
        readonly disabled?: boolean | undefined;
      }
    | {
        readonly type: "input";
        readonly event: string;
        readonly blurEvent?: string | undefined;
        readonly value: string;
        readonly label?: string | undefined;
        readonly placeholder?: string | undefined;
        readonly mono?: boolean | undefined;
        readonly disabled?: boolean | undefined;
        readonly readOnly?: boolean | undefined;
      }
    | {
        readonly type: "toggle";
        readonly event: string;
        readonly blurEvent?: string | undefined;
        readonly value: boolean;
        readonly label: string;
        readonly disabled?: boolean | undefined;
      }
    | { readonly type: "list"; readonly items: readonly UiListItem[] }
    | { readonly type: "empty"; readonly text: string }
  );

/*
  The inventory and the union are pinned to each other the way the instance frames are:
  `satisfies` blocks a name the union lacks, `Exclude` blocks a kind the inventory forgot.
 */
type MissingUiNodeType = Exclude<UiNode["type"], UiNodeType>;
const uiNodeInventoryComplete: MissingUiNodeType extends never ? true : never = true;
void uiNodeInventoryComplete;

/*
  The recursion is declared WITHOUT the size bound, and the bound is applied once at the root
  below: a check attached to the recursive schema itself would re-walk every subtree at every
  level, which is quadratic in depth for no extra refusal.
 */
const uiNode: z.ZodType<UiNode> = z.lazy(() =>
  z.discriminatedUnion("type", [
    z.strictObject({
      ...uiNodeMeta,
      type: z.literal("box"),
      direction: z.enum(["row", "column"]).optional(),
      gap: z.literal([0, 1, 2, 3, "adaptive"]).optional(),
      gapRem: z.number().min(0).max(4).optional(),
      align: z.enum(["start", "center", "end", "stretch"]).optional(),
      justify: z.enum(["start", "center", "end", "between"]).optional(),
      grow: z.boolean().optional(),
      wrap: z.boolean().optional(),
      children: z.array(uiNode).max(MAX_UI_NODES),
    }),
    z.strictObject({
      ...uiNodeMeta,
      type: z.literal("heading"),
      text: uiText,
      level: z.literal([1, 2, 3]).optional(),
    }),
    z.strictObject({
      ...uiNodeMeta,
      type: z.literal("text"),
      text: uiText,
      tone: UiToneSchema.optional(),
      mono: z.boolean().optional(),
      wrap: z.boolean().optional(),
      strong: z.boolean().optional(),
      grow: z.boolean().optional(),
    }),
    z.strictObject({
      ...uiNodeMeta,
      type: z.literal("code"),
      text: z.string().max(MAX_UI_CODE_LENGTH),
    }),
    z.strictObject({
      ...uiNodeMeta,
      type: z.literal("badge"),
      text: uiText,
      tone: UiToneSchema.optional(),
    }),
    z.strictObject({ ...uiNodeMeta, type: z.literal("icon"), icon: UiIconSchema }),
    z.strictObject({ ...uiNodeMeta, type: z.literal("divider") }),
    z.strictObject({ ...uiNodeMeta, type: z.literal("spinner"), label: uiText.optional() }),
    z.strictObject({
      ...uiNodeMeta,
      type: z.literal("button"),
      label: uiText,
      event: uiEventName,
      blurEvent: uiEventName.optional(),
      payload: z.unknown().optional(),
      tone: UiToneSchema.optional(),
      disabled: z.boolean().optional(),
      expanded: z.boolean().optional(),
      action: z.string().min(1).max(96).optional(),
      icon: UiIconSchema.optional(),
      iconOnly: z.boolean().optional(),
    }),
    z.strictObject({
      ...uiNodeMeta,
      type: z.literal("select"),
      event: uiEventName,
      blurEvent: uiEventName.optional(),
      value: z.string().max(MAX_UI_TEXT_LENGTH).nullable(),
      options: z
        .array(z.strictObject({ value: z.string().max(MAX_UI_TEXT_LENGTH), label: uiText }))
        .max(MAX_UI_OPTIONS),
      label: uiText.optional(),
      disabled: z.boolean().optional(),
    }),
    z.strictObject({
      ...uiNodeMeta,
      type: z.literal("input"),
      event: uiEventName,
      blurEvent: uiEventName.optional(),
      value: z.string().max(MAX_UI_TEXT_LENGTH),
      label: uiText.optional(),
      placeholder: uiText.optional(),
      mono: z.boolean().optional(),
      disabled: z.boolean().optional(),
      readOnly: z.boolean().optional(),
    }),
    z.strictObject({
      ...uiNodeMeta,
      type: z.literal("toggle"),
      event: uiEventName,
      blurEvent: uiEventName.optional(),
      value: z.boolean(),
      label: uiText,
      disabled: z.boolean().optional(),
    }),
    z.strictObject({
      ...uiNodeMeta,
      type: z.literal("list"),
      items: z
        .array(
          z.strictObject({
            key: z.string().min(1).max(128),
            primary: uiText,
            secondary: uiText.optional(),
            tone: UiToneSchema.optional(),
            event: uiEventName.optional(),
            payload: z.unknown().optional(),
          }),
        )
        .max(MAX_UI_LIST_ITEMS),
    }),
    z.strictObject({ ...uiNodeMeta, type: z.literal("empty"), text: uiText }),
  ]),
);

/** Walks a parsed tree once; returns the first bound broken, or null when it fits. */
function uiTreeOverflow(root: UiNode): string | null {
  let count = 0;
  const walk = (node: UiNode, depth: number): string | null => {
    if (depth > MAX_UI_DEPTH) return `nests deeper than ${String(MAX_UI_DEPTH)} levels`;
    if (++count > MAX_UI_NODES) return `carries more than ${String(MAX_UI_NODES)} nodes`;
    if (node.type !== "box") return null;
    for (const child of node.children) {
      const overflow = walk(child, depth + 1);
      if (overflow !== null) return overflow;
    }
    return null;
  };
  return walk(root, 1);
}

/**
 * A whole tree, as a `render` frame carries it: every node one of the vocabulary's kinds,
 * every object strict, at most `MAX_UI_DEPTH` deep and `MAX_UI_NODES` large.
 */
export const UiNodeSchema: z.ZodType<UiNode> = uiNode.check((ctx) => {
  const overflow = uiTreeOverflow(ctx.value);
  if (overflow === null) return;
  ctx.issues.push({
    code: "custom",
    input: ctx.value,
    message: `ui tree ${overflow}`,
  });
});

/** The identity of one MOUNTED panel: host-chosen, so two tiles of one panel never collide. */
const instanceId = z.string().min(1).max(128);

/**
 * A named callback firing: which mounted instance, which event the control declared, and the
 * payload the control carried. This is the ONLY direction a user's gesture travels into a
 * worker — as data naming an event, never as a DOM event.
 */
const UiEventFields = {
  instance: instanceId,
  event: uiEventName,
  payload: z.unknown().optional(),
} as const;
export const UiEventSchema = z.strictObject(UiEventFields);
export type UiEvent = z.infer<typeof UiEventSchema>;

// ---------------------------------------------------------------------------- runner policy

/**
 * How many crashes a supervisor tolerates before it stops respawning and says so on the
 * roster (`isolate_crashed`, ADR 0016 §6): VS Code's policy, as data. Past the budget the
 * plugin's doors answer `unavailable` until an operator toggles it.
 */
export const ISOLATE_CRASH_BUDGET = { count: 3, windowMs: 300_000 } as const;

/**
 * The bound on one dispatch into a child (ADR 0016 §6, MetaMask's `maxRequestTime` as
 * precedent): a hung isolate is a REFUSAL (`unavailable`), never a stuck promise. Lifecycle
 * hooks keep the engine's own 2 s bound (`LIFECYCLE_TIMEOUT_MS`).
 */
export const ISOLATE_DISPATCH_DEADLINE_MS = 10_000;

/**
 * How long a child may sit without a dispatch before the supervisor shuts it down; the next
 * dispatch respawns it. One process and one heap per installed plugin is the real cost of
 * the boundary (ADR 0016 §The performance bill), and it scales with installs rather than
 * traffic, which is why eviction is stage-1 policy rather than a later optimisation.
 */
export const ISOLATE_IDLE_EVICT_MS = 600_000;

/** The largest artifact an install door will read, from a path or over the network. */
export const ISOLATE_MAX_ARTIFACT_BYTES = 16 * 1024 * 1024;
/** Raw bytes in one server-isolate frame, capped before JSON parsing in either process. */
export const ISOLATE_MAX_FRAME_BYTES = 8 * 1024 * 1024;

// ---------------------------------------------------------------------------- server process frames

/** Correlates a request with its answer on either process direction; sender-chosen, opaque. */
const frameId = z.string().min(1).max(64);

/** Maximum failure prose carried by an isolate frame. */
export const ISOLATE_ERROR_TEXT_MAX = 2_048;
const errorText = z.string().max(ISOLATE_ERROR_TEXT_MAX);

/**
 * The ctx slices a child may CALL BACK into (ADR 0016 §2), each one an RPC the host serves on
 * the plugin's behalf: storage namespaced by plugin id, the plugin's own database when its
 * manifest declared one, the dispatching caller's authority, the two host services a
 * first-party slice already reaches by method name, and — last, because it is the only one
 * that reaches ANOTHER plugin — `actions.call` on a declared dependency's door (ADR 0041),
 * which crosses as the same named refusal it is in realm. Everything else in `ActionCtx` is
 * NOT served in stage 1 — the guest runtime raises `IsolateSliceUnavailable(method)` and maps
 * it to `{ ok: false, rule: "refused" }`, so the absence is a named refusal at the door rather
 * than a hang or a throw.
 */
export const ISOLATE_CTX_METHODS = [
  "storage.get",
  "storage.set",
  "storage.compareAndSet",
  "storage.delete",
  "storage.keys",
  "database.query",
  "database.run",
  "database.batch",
  "auth.allows",
  "outsideScope",
  "newId",
  "machines.isOnline",
  "machines.getTerminalExecution",
  "machines.repository",
  "machines.inventory",
  "machines.drain",
  "identity.enrollMachine",
  "identity.rotateMachineToken",
  "identity.revokeMachine",
  "identity.forgetMachine",
  "identity.mintTokenV2",
  "identity.registerAgentV2",
  "identity.getAgentV2",
  "identity.listAgentsV2",
  "identity.updateAgentV2",
  "identity.disableAgentV2",
  "identity.enableAgentV2",
  "identity.retireAgentV2",
  "identity.createRunV2",
  "identity.createChildRunV2",
  "identity.inspectRunV2",
  "identity.listRunsV2",
  "identity.renewAgentRunV2",
  "identity.finishAgentRunV2",
  "identity.reportRunActivityV2",
  "identity.acknowledgeAgentPolicyV2",
  "identity.listCredentialsV2",
  "placement.place",
  "host.roster",
  "host.enabled",
  "streams.open",
  "streams.publish",
  "streams.close",
  "jobs.describe",
  "jobs.describeDeployment",
  "jobs.execute",
  "jobs.status",
  "jobs.runTerminal",
  "jobs.listRuns",
  "jobs.inspectInputs",
  "jobs.input",
  "jobs.cancel",
  "jobs.output",
  "jobs.outputs",
  "jobs.journal",
  "jobs.schedule",
  "jobs.schedules",
  "jobs.disableSchedule",
  "jobs.follow",
  "jobs.ack",
  "jobs.unfollow",
  "services.describe",
  "services.readConfiguration",
  "services.configureConfiguration",
  "services.read",
  "services.invoke",
  "services.describeInstance",
  "services.listInstances",
  "services.readInstanceConfiguration",
  "services.configureInstance",
  "services.readInstance",
  "services.invokeInstance",
  "actions.call",
] as const;
export const IsolateCtxMethodSchema = z.enum(ISOLATE_CTX_METHODS);
export type IsolateCtxMethod = (typeof ISOLATE_CTX_METHODS)[number];

/** Contract 12: the only host methods available while an action is preparing. */
export const ISOLATE_PREPARATION_METHODS = [
  "prepare.terminals.resolveMachine",
  "prepare.terminals.stored",
  "prepare.containers.placement",
  "prepare.native.demand",
] as const;
export type IsolatePreparationMethod = (typeof ISOLATE_PREPARATION_METHODS)[number];
export const PreparedRequirementSchema = z.strictObject({
  cap: AskableCapSchema,
  node: GrantNodeSchema,
  reach: GrantReachSchema,
});
export const PreparedRequirementsSchema = PreparedRequirementSchema.array().max(64);
export const IsolatePreparationMetadataSchema = z
  .record(
    LocalNameSchema,
    z.strictObject({
      caps: AskableCapSchema.array()
        .max(128)
        .refine((caps) => new Set(caps).size === caps.length, {
          message: "duplicate preparation capability",
        }),
    }),
  )
  .refine((actions) => Object.keys(actions).length <= 128, {
    message: "too many action preparers",
  });
export type IsolatePreparationMetadata = z.infer<typeof IsolatePreparationMetadataSchema>;

/**
 * Contract 12 native preparation discloses selectors, not literal inputs or session contents.
 * The host verifies these against its installation and owner; the digest commits the private
 * full runtime for validation at effect admission. It is not proof that opaque inputs are valid.
 */
export const NativePreparationDemandSchema = z.strictObject({
  machineId: TerminalRuntimeSchema.shape.machineId,
  pluginId: TerminalRuntimeSchema.shape.pluginId,
  operationId: TerminalRuntimeSchema.shape.operationId,
  installationRevision: TerminalRuntimeSchema.shape.installationRevision,
  artifactSha256: TerminalRuntimeSchema.shape.artifactSha256,
  resourceBindingDigest: TerminalRuntimeSchema.shape.resourceBindingDigest,
  inputs: TerminalRuntimeSchema.shape.inputs,
  sessionMachineId: TerminalRuntimeSchema.shape.machineId.optional(),
  runtimeDigest: z.string().regex(/^[a-f0-9]{64}$/),
});
export type NativePreparationDemand = z.infer<typeof NativePreparationDemandSchema>;

export function projectNativePreparationDemand(
  runtime: TerminalRuntime,
  runtimeDigest: string,
): NativePreparationDemand {
  return NativePreparationDemandSchema.parse({
    machineId: runtime.machineId,
    pluginId: runtime.pluginId,
    operationId: runtime.operationId,
    installationRevision: runtime.installationRevision,
    artifactSha256: runtime.artifactSha256,
    resourceBindingDigest: runtime.resourceBindingDigest,
    ...(runtime.inputs === undefined ? {} : { inputs: runtime.inputs }),
    ...(runtime.session === undefined ? {} : { sessionMachineId: runtime.session.machineId }),
    runtimeDigest,
  });
}

export const IsolatePreparationArgsSchemas = {
  "prepare.terminals.resolveMachine": z.tuple([
    z.strictObject({
      machineId: z.string().min(1).max(128).optional(),
      runtimeMachineId: z.string().min(1).max(128).optional(),
    }),
  ]),
  "prepare.terminals.stored": z.tuple([z.string().min(1).max(128)]),
  "prepare.containers.placement": z.tuple([z.string().min(1).max(128)]),
  "prepare.native.demand": z.tuple([
    NativePreparationDemandSchema,
    z.string().min(1).max(128),
    z.string().min(1).max(128),
  ]),
} as const;
export const IsolatePreparationResultSchemas = {
  "prepare.terminals.resolveMachine": z.strictObject({
    machineId: z.string().min(1).max(128),
    terminalHostId: z.string().min(1).max(128).nullable(),
    terminalExecution: TerminalExecutionSchema.nullable(),
  }),
  "prepare.terminals.stored": z
    .strictObject({
      machineId: z.string().min(1).max(128),
      containerId: z.string().min(1).max(128),
      governed: z.boolean(),
      nativeRequirements: PreparedRequirementsSchema.optional(),
    })
    .nullable(),
  "prepare.containers.placement": z.enum(["element", "tile"]),
  "prepare.native.demand": PreparedRequirementsSchema,
} as const;

/** The four lifecycle hooks a server half may declare; `purge` never crosses (it is the host's). */
export const ISOLATE_HOOKS = [
  "onEnable",
  "onDisable",
  "onAssemblyChanged",
  "onJobSettled",
] as const;
export const IsolateHookSchema = z.enum(ISOLATE_HOOKS);
export type IsolateHook = (typeof ISOLATE_HOOKS)[number];

/** Named guest transformations: only metadata crosses the handshake, never callbacks. */
export const MAX_ISOLATE_MIGRATIONS = 128;
export const ISOLATE_MIGRATION_DEADLINE_MS = 10_000;
export const MAX_MIGRATION_STORAGE_OPERATIONS = 65_536;
export const IsolateMigrationSchema = z.strictObject({
  // Strict end of input: `$` alone would also accept a trailing line terminator.
  name: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,127}(?![\s\S])/),
  to: PluginDataVersionSchema,
});
export const IsolateMigrationsSchema = IsolateMigrationSchema.array()
  .max(MAX_ISOLATE_MIGRATIONS)
  .refine((migrations) => new Set(migrations.map(({ name }) => name)).size === migrations.length, {
    message: "duplicate migration name",
  });
export const GuestMigrationDeclarationsSchema = z
  .strictObject({
    dataVersion: PluginDataVersionSchema.optional(),
    migrations: IsolateMigrationsSchema,
  })
  .refine(
    ({ dataVersion, migrations }) =>
      migrations.every(
        ({ to }) =>
          dataVersion !== undefined &&
          (to.major < dataVersion.major ||
            (to.major === dataVersion.major && to.minor <= dataVersion.minor)),
      ),
    { message: "migration target requires a dataVersion at or above its target" },
  );

/**
 * The two rungs a CHILD may answer: it parses arguments against the action's own zod input
 * (the schema lives where the code lives), and its handler may refuse on domain grounds. Every
 * other rung is the host's — a child cannot say `forbidden` about a caller it never graded.
 */
export const ISOLATE_GUEST_DENIAL_RULES = [
  "invalid_args",
  "refused",
] as const satisfies readonly ActionDenialRule[];
export type IsolateGuestDenialRule = (typeof ISOLATE_GUEST_DENIAL_RULES)[number];

/**
 * What changed in one roster commit, as a child's `onAssemblyChanged` is told. The wire twin
 * of `@manifold/plugin`'s `AssemblyDelta`: both lists in composition order.
 */
export const AssemblyDeltaSchema = z.strictObject({
  enabled: PluginIdSchema.array(),
  disabled: PluginIdSchema.array(),
});
export type AssemblyDelta = z.infer<typeof AssemblyDeltaSchema>;

/**
 * The CALLER, as one dispatch carries it into the child: who asked, what they hold, and the
 * clock — the pieces of `ActionCtx` that are plain data. `auth.allows` is not among them
 * because it consults grants the child never sees; it is served as a call back.
 */
export const IsolateDispatchCtxSchema = z.strictObject({
  traceId: z.number().int().positive(),
  /** Host-derived immediate plugin caller for contract 8+; omitted entirely for older guests. */
  callerPlugin: PluginIdSchema.nullable().optional(),
  principal: PrincipalSchema,
  caps: CapSchema.array(),
  isRoot: z.boolean(),
  containerScope: z.string().min(1).nullable(),
  /** Sent only to contract 6+, derived from authenticated Run authority. */
  agentRun: z
    .strictObject({
      runId: z.string().min(1).max(128),
      agentId: z.string().min(1).max(128),
    })
    .nullable()
    .optional(),
  now: z.number().int().min(0),
});
export type IsolateDispatchCtx = z.infer<typeof IsolateDispatchCtxSchema>;

/** The public harness verbs, with no credential material on the wire. */
export const IsolateHarnessRequestSchema = z.discriminatedUnion("method", [
  z.strictObject({ method: z.literal("validateProfile"), profile: z.unknown() }),
  z.strictObject({
    method: z.literal("launch"),
    run: AgentRunSchema,
    agent: AgentSchema,
    target: HarnessTargetSchema,
  }),
  z.strictObject({ method: z.literal("sessions"), target: HarnessTargetSchema }),
  z.strictObject({ method: z.literal("resolveSession"), ref: SessionRefSchema }),
  z.strictObject({
    method: z.literal("send"),
    run: AgentRunSchema,
    input: SendRunInputRequestSchema.shape.input,
  }),
  /** Contract 13+, and only to a guest whose `loaded` frame declared `harnessResolvesModel`. */
  z.strictObject({ method: z.literal("resolveModel"), run: AgentRunSchema, model: RunModelSchema }),
]);
export type IsolateHarnessRequest = z.infer<typeof IsolateHarnessRequestSchema>;

/** Checked on both ends before accepting a harness result or staging its emissions. */
export const IsolateHarnessResultSchemas = {
  validateProfile: z.null(),
  launch: z.strictObject({
    runtime: TerminalRuntimeSchema,
    session: SessionRefSchema,
    reviewDigest: z.string().regex(/^[a-f0-9]{64}$/),
  }),
  // One extra entry preserves the host's 100-item inventory truncation signal.
  sessions: z.array(SessionRefSchema).max(101),
  resolveSession: SessionRefSchema.nullable(),
  send: z.null(),
  resolveModel: RunModelSchema.nullable(),
} as const;

/**
 * An answer to a `call`, in either direction on either boundary — one shape, because a
 * worker asking the page for `machines()` and a child asking the server for `storage.get`
 * are the same conversation: an id, and a result or an error sentence.
 */
export const IsolateReplyFrameSchema = z.discriminatedUnion("ok", [
  z.strictObject({ t: z.literal("reply"), id: frameId, ok: z.literal(true), result: z.unknown() }),
  z.strictObject({ t: z.literal("reply"), id: frameId, ok: z.literal(false), error: errorText }),
]);
export type IsolateReplyFrame = z.infer<typeof IsolateReplyFrameSchema>;

/** At most this many positional arguments ride one `call`; every served method takes fewer. */
const callArgs = z.array(z.unknown()).max(8);

/**
 * HOST → CHILD as receipt-bearing, bounded newline-delimited JSON. `load` is the first frame and
 * names the extracted bundle directory the child already runs from; `dispatch` is one action
 * with the caller's authority captured per id, so a `call` the child makes while handling it
 * is graded as THAT caller; `hook` is a lifecycle fan-out; `reply` answers a child's `call`;
 * `shutdown` asks for an orderly exit, and is also what idle eviction sends.
 */
export const IsolateHostFrameSchema = z.discriminatedUnion("t", [
  z.strictObject({
    t: z.literal("load"),
    pluginId: PluginIdSchema,
    manifest: PluginManifestSchema,
    dir: z.string().min(1).max(4096),
    hardenedContract: z.number().int().positive().optional(),
  }),
  z.strictObject({
    t: z.literal("dispatch"),
    id: frameId,
    /** The LOCAL action name: the child knows its own plugin id from `load`. */
    action: LocalNameSchema,
    args: z.unknown(),
    ctx: IsolateDispatchCtxSchema,
  }),
  z
    .strictObject({
      t: z.literal("harness"),
      id: frameId,
      request: IsolateHarnessRequestSchema,
      ctx: IsolateDispatchCtxSchema.optional(),
    })
    .refine(({ request, ctx }) => (request.method === "validateProfile") === (ctx === undefined), {
      message: "only profile validation omits caller context",
    }),
  /** Resumes this same dispatch after the guest parsed and the host admitted its input. */
  z.strictObject({
    t: z.literal("admitted"),
    id: frameId,
    allowed: z.boolean(),
  }),
  z.strictObject({
    t: z.literal("hook"),
    id: frameId,
    hook: IsolateHookSchema,
    delta: AssemblyDeltaSchema.optional(),
    job: SettledJobSchema.optional(),
    /**
     * Whether the host serves `jobs.*` for THIS hook: true when a credential was restored for
     * it, absent when none could be. `onJobSettled` never carries it — its slice rides the
     * settled job's own credential and is implied by the hook's name — so the flag says
     * exactly one thing: an ordinary lifecycle hook has an installer's authority behind it.
     */
    jobs: z.boolean().optional(),
    /** Contract 11+: credential-bound, read-only host, fleet and instance-service metadata. */
    metadata: z.boolean().optional(),
  }),
  z.strictObject({
    t: z.literal("migrate"),
    id: frameId,
    migration: IsolateMigrationSchema,
  }),
  z.strictObject({
    t: z.literal("job_update"),
    id: frameId,
    delivery: z.number().int().positive(),
    update: JobFollowUpdateSchema,
  }),
  z.strictObject({ t: z.literal("producer_closed"), id: frameId }),
  IsolateReplyFrameSchema,
  z.strictObject({ t: z.literal("shutdown") }),
]);
export type IsolateHostFrame = z.infer<typeof IsolateHostFrameSchema>;

/** Raw process transport envelope: the receipt proves the child consumed this host frame. */
export const IsolateHostEnvelopeSchema = z.strictObject({
  receipt: z.uuid(),
  frame: IsolateHostFrameSchema,
});
export type IsolateHostEnvelope = z.infer<typeof IsolateHostEnvelopeSchema>;

/** The largest action list a child may announce — the roster publishes every one of them. */
export const MAX_ISOLATE_ACTIONS = 128;
/** Emissions one dispatch may stage; in-realm handlers have no bound because they are trusted. */
export const MAX_ISOLATE_EMITS = 256;

const IsolateDispatchOutcomeSchema = z.discriminatedUnion("ok", [
  z.strictObject({
    ok: z.literal(true),
    result: z.unknown(),
    emits: z
      .array(
        z.strictObject({
          ref: ManifoldRefSchema,
          kind: EventKindSchema,
          payload: EventPayloadSchema,
        }),
      )
      .max(MAX_ISOLATE_EMITS),
  }),
  z.strictObject({
    ok: z.literal(false),
    rule: z.enum(ISOLATE_GUEST_DENIAL_RULES),
    message: errorText,
  }),
]);

/**
 * CHILD → HOST. `received` proves the child consumed one unpredictable host envelope.
 * `loaded` answers `load` with the actions the child serves — `input` and `result` as JSON
 * Schema (`z.toJSONSchema` of the child's own zod), which is what the roster publishes for
 * them — and which hooks it declared, so the host fans out only what exists. `dispatched`
 * carries the handler's outcome AND the emissions it staged: the host re-stages them through
 * its own `ctx.emit` so the ledger settles before any subscriber hears (A6). `call` is the
 * child reaching a served ctx slice.
 */
export const IsolateChildFrameSchema = z.discriminatedUnion("t", [
  z.strictObject({ t: z.literal("received"), receipt: z.uuid() }),
  z.strictObject({
    t: z.literal("loaded"),
    actions: ActionSummarySchema.array().max(MAX_ISOLATE_ACTIONS),
    hooks: z.strictObject({
      onEnable: z.boolean(),
      onDisable: z.boolean(),
      onAssemblyChanged: z.boolean(),
      onJobSettled: z.boolean(),
    }),
    migrations: IsolateMigrationsSchema.optional(),
    harness: HarnessDefinitionSchema.optional(),
    /** Contract 13+: the declared harness implements `resolveModel`. */
    harnessResolvesModel: z.literal(true).optional(),
    /** Must equal the artifact's sealed server binding. */
    prepareActions: IsolatePreparationMetadataSchema.optional(),
  }),
  z.strictObject({ t: z.literal("load_failed"), error: errorText }),
  /** Only declared authority targets cross; transformed handler arguments stay in the guest. */
  z.strictObject({
    t: z.literal("prepared"),
    id: frameId,
    targets: z.array(ManifoldRefSchema.nullable()).max(64),
    additionalRequirements: PreparedRequirementsSchema.optional(),
  }),
  z.strictObject({
    t: z.literal("dispatched"),
    id: frameId,
    outcome: IsolateDispatchOutcomeSchema,
  }),
  z.strictObject({
    t: z.literal("harnessed"),
    id: frameId,
    outcome: IsolateDispatchOutcomeSchema,
  }),
  z.strictObject({
    t: z.literal("hooked"),
    id: frameId,
    ok: z.boolean(),
    error: errorText.optional(),
  }),
  z.strictObject({
    t: z.literal("migrated"),
    id: frameId,
    name: IsolateMigrationSchema.shape.name,
    outcome: z.discriminatedUnion("ok", [
      z.strictObject({ ok: z.literal(true) }),
      z.strictObject({ ok: z.literal(false), error: errorText }),
    ]),
  }),
  z.strictObject({
    t: z.literal("call"),
    id: frameId,
    method: z.union([IsolateCtxMethodSchema, z.enum(ISOLATE_PREPARATION_METHODS)]),
    args: callArgs,
  }),
]);
export type IsolateChildFrame = z.infer<typeof IsolateChildFrameSchema>;

// ---------------------------------------------------------------------------- web worker frames

/**
 * The host services a Worker may call back into, by name — the same semantics as
 * `SessionHandle`'s methods of those names (the terminal three arrive with #196). The page
 * serves each from the panel's REAL `HostServices`, so the worker never holds the token
 * (ADR 0016 §3): it calls the door through the host, which attaches the caller's authority.
 */
export const WEB_HOST_METHODS = [
  "action",
  "place",
  "selfCaps",
  "machines",
  "resolve",
  "navigate",
  "openTerminal",
  "sendTerminalInput",
  "terminalsByContainer",
  "openStream",
  "closeStream",
  "ackStream",
  "subscribe",
  "unsubscribe",
  "ackEvent",
  "syncSubscriptions",
  "createTerminal",
] as const;

/** Mounted-view facts only. A current host call, not this snapshot, decides authority. */
export const WebHostContextSchema = z.strictObject({
  principal: PrincipalSchema,
  caps: CapSchema.array(),
  workspaceCaps: CapSchema.array().optional(),
  workspaceEvents: z.boolean().optional(),
  /** Changes only when the page replaces this mount's actual client binding. */
  clientEpoch: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER).optional(),
  containerId: z.string().min(1).nullable(),
  topics: z.strictObject({
    index: ManifoldRefSchema.array().max(64),
    terminals: ManifoldRefSchema.array().max(64),
    attendance: ManifoldRefSchema.array().max(64),
    machines: ManifoldRefSchema.array().max(64),
  }),
  status: z.enum(["idle", "connecting", "open", "reconnecting", "closed"]),
  hidden: z.boolean(),
  canAuthor: z.boolean(),
});
export type WebHostContext = z.infer<typeof WebHostContextSchema>;
export const WebHostMethodSchema = z.enum(WEB_HOST_METHODS);
export type WebHostMethod = (typeof WEB_HOST_METHODS)[number];

/**
 * PAGE → WORKER over `postMessage`. `init` is the first frame: who the viewer is and what
 * they hold, as data, so the guest can shape its UI without asking; `mount`/`unmount` bracket
 * one panel instance's life in a tile; `event` is a named callback firing; `reply` answers
 * the worker's `call`.
 */
export const WebIsolateHostFrameSchema = z.discriminatedUnion("t", [
  z.strictObject({
    t: z.literal("init"),
    pluginId: PluginIdSchema,
    principal: PrincipalSchema,
    caps: CapSchema.array(),
    workspaceCaps: CapSchema.array().optional(),
    workspaceEvents: z.boolean().optional(),
    containerId: z.string().min(1).nullable(),
  }),
  z.strictObject({
    t: z.literal("mount"),
    instance: instanceId,
    panel: LocalNameSchema,
    kind: z.enum(["panel", "section"]).optional(),
    context: WebHostContextSchema.optional(),
    arg: PanelArgSchema.refine(
      validPanelArg,
      "panel argument must be bounded JSON data",
    ).optional(),
  }),
  z.strictObject({
    t: z.literal("context"),
    instance: instanceId,
    context: WebHostContextSchema,
    arg: PanelArgSchema.refine(
      validPanelArg,
      "panel argument must be bounded JSON data",
    ).optional(),
  }),
  z.strictObject({ t: z.literal("notification"), id: frameId }),
  z.strictObject({ t: z.literal("unmount"), instance: instanceId }),
  z.strictObject({ t: z.literal("event"), ...UiEventFields }),
  z.strictObject({ t: z.literal("stream"), id: frameId, message: StreamServerMessageSchema }),
  IsolateReplyFrameSchema,
]);
export type WebIsolateHostFrame = z.infer<typeof WebIsolateHostFrameSchema>;

/** The panel ids a worker serves; bounded as the manifest bounds `contributes.panels`. */
export const MAX_ISOLATE_PANELS = 8;

/**
 * WORKER → PAGE. `ready` names the panels the guest serves, so a manifest panel with no
 * program is a named absence rather than a blank tile; `render` is the whole tree for one
 * instance, replaced wholesale — the host diffs, the worker never patches; `call` reaches a
 * host method; `fault` is the worker's own report that a program threw, scoped to an instance
 * when one was involved. A fault is per-session and touches no roster row (§6: the roster
 * says what the SERVER knows, and a browser's failure is not that).
 */
export const WebIsolateWorkerFrameSchema = z.discriminatedUnion("t", [
  z.strictObject({
    t: z.literal("ready"),
    panels: LocalNameSchema.array().max(MAX_ISOLATE_PANELS),
    sections: LocalNameSchema.array().max(MAX_ISOLATE_PANELS).optional(),
    hardenedContract: z.number().int().positive().optional(),
  }),
  z.strictObject({ t: z.literal("render"), instance: instanceId, tree: UiNodeSchema }),
  z.strictObject({
    t: z.literal("call"),
    id: frameId,
    instance: instanceId.optional(),
    method: WebHostMethodSchema,
    args: callArgs,
  }),
  z.strictObject({ t: z.literal("fault"), instance: instanceId.optional(), error: errorText }),
]);
export type WebIsolateWorkerFrame = z.infer<typeof WebIsolateWorkerFrameSchema>;

// ---------------------------------------------------------------------------- the artifact

/**
 * The artifact format an install door reads: ONE JSON document, `<id>.manifold-plugin.json`.
 * JSON rather than a tarball because the protocol package's whole runtime dependency budget is
 * zod (docs/CONTRACTS.md §Dependency decisions) and a hand-rolled tar reader is a worse artifact than base64. `sha256` on
 * the roster row is over the file's exact BYTES, never over this parsed form, so re-hashing at
 * boot compares what was installed with what is on disk (ADR 0016 §8 stage 3, R8: fail-closed).
 * The literal is the whole versioning story: a reader that meets a format it does not know
 * refuses `artifact_invalid` and says which.
 */
export const PLUGIN_BUNDLE_FORMAT = 1;

/**
 * Hardened bundle compatibility is independent of the artifact format and machine/session
 * channels. Add an additive-optional version to the set; reset the set only on a real break.
 * A host emits a newer field only to the contract that understands it.
 *
 * HISTORY
 * 1: The bounded receipt transport (#536) and prepared/admitted dispatch (#587) baseline.
 *    Earlier, unstamped bundles are not this baseline and require one repack.
 * 1 -> 2: Additive-optional `load.hardenedContract` identifies the admitted bundle contract.
 *    Contract-1 guests still receive the original load frame and retain prepared admission.
 * 2 -> 3: Additive-optional action result projections in loaded action summaries. Hosts still
 *    admit contract-1/2 guests and send each guest its own admitted contract, never the latest.
 * 3 -> 4: Additive metadata-only `jobs.inspectInputs` for reviewed bound sources. Older
 *    guests retain their existing methods; inspection never transfers execution authority.
 * 4 -> 5: Additive-optional `resultProjection.textFields` marks reviewed string-or-null leaves.
 *    Older guests omit it and keep their exact declaration digests and result behavior.
 *    Hosts retain contracts 1/2/3/4 and send only the guest's admitted load stamp.
 * 5 -> 6: Additive authenticated agent-run context and run-access declarations.
 * 6 -> 7: Additive harness metadata and correlated harness calls; profile validation has no ctx.
 * 7 -> 8: Additive-optional `ctx.callerPlugin` names the immediate calling plugin or null for
 *    non-plugin entry. Hosts omit it for older admitted guests, including strict contract-1 parsers.
 * 8 -> 9: Real React frame roots, section contributions and mounted host context; portable
 *    web.worker.js artifacts, event invalidations, authoring and narrow machine bridges.
 *    Legacy web guests keep their original init/mount shapes and existing control frames.
 * 9 -> 10: Optional live `physicalCoreCount` in machine inventory (#939). Hosts omit it
 *    for older admitted guests, whose strict inventory parser predates the field.
 * 10 -> 11: Additive-optional hook.metadata announces read-only lifecycle host/fleet/service
 *    metadata. Older packed strict guests retain their original hook frames.
 * 11 -> 12: Live workspace authority, subscription-ordering fences, scoped identity RPC
 *    and sealed read-only action preparation. Older guests retain their released frames
 *    and legacy capability vocabulary.
 * 12 -> 13: Additive-optional harness `resolveModel`, announced by `loaded.harnessResolvesModel`,
 *    and an optional `model` on `identity.reportRunActivityV2`. Hosts never send the verb to
 *    older guests, whose Run model reports refuse as unverifiable.
 */
export const HARDENED_CONTRACT_VERSION = 13;
export const HARDENED_CONTRACT_COMPAT_VERSIONS: ReadonlySet<number> = new Set([
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13,
]);
export const HARDENED_CONTRACT_MINIMUM = Math.min(...HARDENED_CONTRACT_COMPAT_VERSIONS);

/**
 * The file the server guest lives in when `entry.server` is true. A NAME rather than a field
 * because the bundle is self-contained — the kit's `pack` inlines the guest runtime into it —
 * and `Bun.spawn(["bun", "--smol", "<dir>/server.js"])` is the whole loader.
 */
export const PLUGIN_BUNDLE_SERVER_FILE = "server.js";

/** Self-contained portable React entry, selected only for a hardened browser contribution. */
export const PLUGIN_BUNDLE_WEB_WORKER_FILE = "web.worker.js";

/**
 * The file the web half's skin lives in when `entry.styles` is true (ADR 0025 §7, #258): one
 * fixed name for the same reason as the server's — the loader fetches it by name at
 * `GET /api/plugins/:id/styles.css`, and the hub reads it by name to admit it. An OPTIONAL
 * member of an already-open `files` record, so a bundle written before it existed parses
 * unchanged and the format literal did not move.
 */
export const PLUGIN_BUNDLE_STYLES_FILE = "styles.css";

/**
 * A member's name inside the bundle: FLAT, one path segment, no leading dot. The files are
 * extracted beside the artifact into `<sha256>/`, so a name that could climb (`../`), nest, or
 * hide (`.env`) is refused at the schema rather than trusted to the extractor (docs/CONTRACTS.md §Data and credential boundaries).
 */
export const PLUGIN_BUNDLE_FILE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/;
export const PluginBundleFileSchema = z.string().regex(PLUGIN_BUNDLE_FILE_PATTERN);
export const MAX_PLUGIN_BUNDLE_FILES = 64;

/**
 * The artifact, parsed. `manifest.entry` is required even for a machine-only plugin, where
 * it is empty. A bundle must declare an executable web, server or machine half; every
 * member those halves name must be present, so a plugin cannot discover its own missing
 * code only at enable time. A declared sheet must also be present, beside a web half to
 * dress. Members are base64 of the file's bytes, each
 * bounded by the artifact cap because nothing inside an artifact can be larger than the
 * artifact.
 */
export const PluginBundleSchema = z
  .strictObject({
    format: z.literal(PLUGIN_BUNDLE_FORMAT),
    /** Absent on legacy artifacts so assembly can hold them with repacking guidance. */
    hardenedContract: z.number().int().positive().optional(),
    /** Included in the artifact hash and checked against every loaded registration. */
    serverBinding: z
      .strictObject({
        prepareActions: IsolatePreparationMetadataSchema,
      })
      .optional(),
    /*
      `safeExtend`, not `extend`: the manifest carries a refinement of its own (a capability
      must be the engine's or the declaring plugin's, ADR 0035), and zod refuses to overwrite
      a key on a refined object through `extend` precisely because the discarded checks would
      be invisible. Requiring `entry` here must not buy a manifest that skips its namespace
      rule — a BUNDLE is exactly the manifest a stranger wrote.
    */
    manifest: PluginManifestSchema.safeExtend({ entry: PluginEntrySchema }),
    /** Shared React and floor-package versions used at build time (ADR 0025). */
    builtAgainst: z.record(z.string(), z.string()).optional(),
    files: z
      .record(PluginBundleFileSchema, z.base64().max(ISOLATE_MAX_ARTIFACT_BYTES))
      .check((ctx) => {
        const count = Object.keys(ctx.value).length;
        if (count <= MAX_PLUGIN_BUNDLE_FILES) return;
        ctx.issues.push({
          code: "custom",
          input: ctx.value,
          message: `bundle carries ${String(count)} files, at most ${String(MAX_PLUGIN_BUNDLE_FILES)} are allowed`,
        });
      }),
  })
  .check((ctx) => {
    const files = ctx.value.files;
    if (ctx.value.serverBinding !== undefined) {
      if ((ctx.value.hardenedContract ?? 0) < 12 || ctx.value.manifest.entry.server !== true) {
        ctx.issues.push({
          code: "custom",
          input: ctx.value,
          path: ["serverBinding"],
          message: "sealed action preparation requires a server half and hardened contract 12",
        });
      }
      for (const [name, preparation] of Object.entries(ctx.value.serverBinding.prepareActions)) {
        if (preparation.caps.some((cap) => !ctx.value.manifest.capabilities.includes(cap))) {
          ctx.issues.push({
            code: "custom",
            input: ctx.value,
            path: ["serverBinding", "prepareActions", name, "caps"],
            message: "preparation capability is not declared by the manifest",
          });
        }
      }
    }
    if (
      Object.values(files).reduce((bytes, data) => bytes + data.length, 0) >
      ISOLATE_MAX_ARTIFACT_BYTES
    ) {
      ctx.issues.push({
        code: "custom",
        input: ctx.value,
        path: ["files"],
        message: "bundle members exceed the artifact byte budget",
      });
    }
    for (const artifact of machineArtifacts(ctx.value.manifest.machine)) {
      if (artifact.bundleFile === undefined) continue;
      const data = files[artifact.bundleFile];
      const bytes =
        data === undefined
          ? 0
          : (data.length / 4) * 3 - (data.endsWith("==") ? 2 : data.endsWith("=") ? 1 : 0);
      if (!Object.hasOwn(files, artifact.bundleFile) || bytes === 0 || bytes > artifact.maxBytes) {
        ctx.issues.push({
          code: "custom",
          input: ctx.value,
          path: ["manifest", "machine"],
          message: "machine bundle member is missing, empty, or exceeds maxBytes",
        });
      }
    }
    const { entry } = ctx.value.manifest;
    const missing = (name: string, half: string): void => {
      ctx.issues.push({
        code: "custom",
        input: ctx.value,
        path: ["manifest", "entry", half],
        message: `entry.${half} names "${name}" but files has no such member`,
      });
    };
    if (
      entry.server !== true &&
      entry.web === undefined &&
      ctx.value.manifest.machine === undefined
    ) {
      ctx.issues.push({
        code: "custom",
        input: ctx.value,
        path: ["manifest", "entry"],
        message: "a bundle must declare an executable web, server or machine half",
      });
      return;
    }
    if (entry.server === true && !Object.hasOwn(ctx.value.files, PLUGIN_BUNDLE_SERVER_FILE)) {
      missing(PLUGIN_BUNDLE_SERVER_FILE, "server");
    }
    if (entry.web !== undefined && !Object.hasOwn(ctx.value.files, entry.web)) {
      missing(entry.web, "web");
    }
    if (entry.worker === true) {
      if (entry.web === undefined || entry.web === PLUGIN_BUNDLE_WEB_WORKER_FILE) {
        ctx.issues.push({
          code: "custom",
          input: ctx.value,
          path: ["manifest", "entry", "worker"],
          message: "a portable Worker requires a separate in-realm web entry",
        });
      }
      if ((ctx.value.hardenedContract ?? 0) < 9) {
        ctx.issues.push({
          code: "custom",
          input: ctx.value,
          path: ["hardenedContract"],
          message: "portable React requires hardened contract 9 or newer",
        });
      }
      if (!Object.hasOwn(files, PLUGIN_BUNDLE_WEB_WORKER_FILE))
        missing(PLUGIN_BUNDLE_WEB_WORKER_FILE, "worker");
    }
    if (entry.styles !== true) return;
    if (entry.web === undefined) {
      ctx.issues.push({
        code: "custom",
        input: ctx.value,
        path: ["manifest", "entry", "styles"],
        message: "entry.styles names a sheet but no web half wears it",
      });
    } else if (!Object.hasOwn(ctx.value.files, PLUGIN_BUNDLE_STYLES_FILE)) {
      missing(PLUGIN_BUNDLE_STYLES_FILE, "styles");
    }
  });
export type PluginBundle = z.infer<typeof PluginBundleSchema>;

/**
 * The isolation vocabulary, published — beside `pluginVocabulary()` at `GET /api/protocol`.
 * A stranger authoring an out-of-tree plugin learns from one read which component kinds a
 * panel may render and how large a tree may be, which ctx slices and host methods a guest may
 * call, the runner's deadlines, and the artifact shape `pack` must emit (issue #151: "ships
 * the closed component vocabulary as data at `GET /api/protocol`").
 */
export function isolateVocabulary(): Record<string, unknown> {
  return {
    uiTones: UI_TONES,
    uiNodeTypes: UI_NODE_TYPES,
    uiControlKinds: UI_CONTROL_KINDS,
    maxUiDepth: MAX_UI_DEPTH,
    maxUiNodes: MAX_UI_NODES,
    ctxMethods: ISOLATE_CTX_METHODS,
    hostMethods: WEB_HOST_METHODS,
    crashBudget: ISOLATE_CRASH_BUDGET,
    dispatchDeadlineMs: ISOLATE_DISPATCH_DEADLINE_MS,
    migrationDeadlineMs: ISOLATE_MIGRATION_DEADLINE_MS,
    maxMigrations: MAX_ISOLATE_MIGRATIONS,
    maxMigrationStorageOperations: MAX_MIGRATION_STORAGE_OPERATIONS,
    migration: z.toJSONSchema(IsolateMigrationSchema),
    idleEvictMs: ISOLATE_IDLE_EVICT_MS,
    maxArtifactBytes: ISOLATE_MAX_ARTIFACT_BYTES,
    maxFrameBytes: ISOLATE_MAX_FRAME_BYTES,
    hostEnvelope: z.toJSONSchema(IsolateHostEnvelopeSchema),
    bundleFormat: PLUGIN_BUNDLE_FORMAT,
    bundleServerFile: PLUGIN_BUNDLE_SERVER_FILE,
    bundleWebWorkerFile: PLUGIN_BUNDLE_WEB_WORKER_FILE,
    uiEvent: z.toJSONSchema(UiEventSchema),
    bundle: z.toJSONSchema(PluginBundleSchema),
  };
}
